import React, { useState, useEffect, useId } from 'react';
import axios from 'axios';
import { refusalMessage, currentLang } from './docText';
import { LoadError, useConfirm, useIsNarrow, gridCols } from './ui';
import { saveBlob } from './downloadFile';
// DATEDAY: date-only values shown as their calendar day in every timezone.
import { toDisplayDate } from './dateOnly';

const API = '/api';

// POLICYHR2: the write allowlist lives in ./policyRoles.js, NOT here. It was briefly in
// this file, which broke six tests: more than five suites call jest.mock on this module
// and re-declare its exports by hand, so the constant became undefined inside Dashboard
// and the gate threw. A value that gates access must not live in a module tests replace.

export const ENTITIES = [
  'All Entities',
  'Enko Capital Management LLP (UK)',
  'Enko Capital Investments Pty Ltd (SA)',
  'Enko Capital Managers Pty Ltd (SA)',
  'ECM SA Managers (Pty) Ltd',
  'Enko Fund Managers Ltd (MU)',
  'Enko Capital West Africa (CIV)',
  'Enko Capital Central Africa (CMR)',
  'Enko Capital Rwanda Ltd (RW)',
];

const CATEGORIES = [
  'HR',
  'Compliance',
  'Finance',
  'Operations',
  'Conflicts of Interest',
  'Anti-Bribery and Corruption',
  'Personal Account Dealing',
  'Other',
];

function PolicyRepository({ currentUser, userRole, employeeEntity, canManage = false, onBack }) {
  const isManager = canManage;
  // RESPONSIVE Phase 2: the two-column form grid reflows to one column below ~900px.
  const isNarrow = useIsNarrow();
  const formGridStyle = { ...styles.formGrid, gridTemplateColumns: gridCols(isNarrow, '1fr 1fr') };
  // CONFIRMMODAL: styled confirmation replacing this file's native window.confirm.
  // PolicyRepository has no dark mode, so the modal renders in its light variant.
  const [askConfirm, confirmModal] = useConfirm(false);

  const [policies, setPolicies] = useState([]);
  // A11YLABEL: one id prefix per component INSTANCE, so two copies of this
  // form on one page cannot produce duplicate ids.
  const a11yId = useId();
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);   // LOADERR3
  const [filterCategory, setFilterCategory] = useState('All');
  const [filterEntity, setFilterEntity] = useState('All');
  const [showUpload, setShowUpload] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', entity: 'All Entities', category: 'HR' });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [policyLoading, setPolicyLoading] = useState(null);

  // POLICYNAME: name the saved file after the policy's TITLE, not the blob UUID.
  //
  const openPolicy = async (policyId) => {
    setPolicyLoading(policyId);
    try {
      const res = await axios.get(`${API}/policies/${policyId}/view`, { responseType: 'blob' });
      // DLNAME: the ONE shared helper. It sets `download` (which is what makes the browser use the
      // name instead of the blob URL's last segment) from the server's X-Download-Filename header.
      // "Policy" is the neutral fallback the server uses too, and is deliberately never the id.
      saveBlob(res, 'Policy');
    } catch (err) {
      alert('Could not open policy. Please try again.');
    } finally {
      setPolicyLoading(null);
    }
  };

  // LOADERR3: a failed policy fetch previously fell through to the "No policies found." empty
  // state, which is indistinguishable from a genuinely empty repository - the exact gap LOADERR
  // was built to close. loadFailed is cleared on every success, so a recovered retry returns to
  // the normal empty/list rendering.
  const load = () => {
    setLoading(true);
    axios.get(`${API}/policies/`)
      .then(res => { setPolicies(res.data); setLoadFailed(false); setLoading(false); })
      .catch(() => { setLoadFailed(true); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  const visible = policies.filter(p => {
    if (!isManager && employeeEntity) {
      if (p.entity !== 'All Entities' && p.entity !== employeeEntity) return false;
    }
    if (filterCategory !== 'All' && p.category !== filterCategory) return false;
    if (filterEntity !== 'All' && p.entity !== filterEntity) return false;
    return true;
  });

  const grouped = {};
  const entityOrder = ['All Entities', ...ENTITIES.slice(1)];
  entityOrder.forEach(e => { if (visible.some(p => p.entity === e)) grouped[e] = {}; });
  visible.forEach(p => {
    if (!grouped[p.entity]) grouped[p.entity] = {};
    if (!grouped[p.entity][p.category]) grouped[p.entity][p.category] = [];
    grouped[p.entity][p.category].push(p);
  });

  const handleUpload = async () => {
    if (!uploadFile || !form.title.trim()) { setError('Title and file are required.'); return; }
    setError('');
    setUploading(true);
    const fd = new FormData();
    fd.append('title', form.title);
    fd.append('description', form.description);
    fd.append('entity', form.entity);
    fd.append('category', form.category);
    fd.append('file', uploadFile);
    try {
      await axios.post(`${API}/policies/upload`, fd);
      setForm({ title: '', description: '', entity: 'All Entities', category: 'HR' });
      setUploadFile(null);
      setShowUpload(false);
      load();
    } catch (err) {
      // UPLOADREFUSE: the server's real reason.
      setError(refusalMessage(err, currentLang(), 'uploadChecks.uploadFailed'));
    }
    setUploading(false);
  };

  // CONFIRMMODAL: same delete action, styled confirmation instead of the native popup.
  const handleDelete = (id) => {
    askConfirm({
      title: 'Remove policy',
      message: 'Remove this policy from the repository?',
      confirmLabel: 'Remove',
      danger: true,
      onConfirm: async () => {
        await axios.delete(`${API}/policies/${id}`);
        load();
      },
    });
  };

  const handleEditSave = async (id) => {
    try {
      await axios.patch(`${API}/policies/${id}`, editForm);
      setEditingId(null);
      load();
    } catch {
      alert('Update failed.');
    }
  };

  return (
    <div style={styles.container}>
      {confirmModal}

      {onBack && (
        <button
          style={styles.dashboardBackBtn}
          onClick={onBack}
          onMouseEnter={e => { e.currentTarget.style.color = '#c9a84c'; }}
          onMouseLeave={e => { e.currentTarget.style.color = '#1a1a2e'; }}
        >
          ← Back to dashboard
        </button>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div>
            <h1 style={styles.title}>Policy Repository</h1>
            <p style={styles.subtitle}>
              {isManager
                ? 'Manage and publish company policies'
                : 'Company policies applicable to your entity'}
            </p>
          </div>
        </div>
        {isManager && (
          <button style={showUpload ? styles.cancelBtn : styles.uploadBtn}
            onClick={() => { setShowUpload(!showUpload); setError(''); }}>
            {showUpload ? 'Cancel' : '+ Upload Policy'}
          </button>
        )}
      </div>

      {/* Data retention transparency notice (Compliance's transparency requirement). Placed here,
          not behind a new route/nav item, so every employee sees it via a screen they already
          reach today - visible regardless of isManager, unlike the upload/manage controls below. */}
      <div style={styles.retentionNotice}>
        <p style={styles.retentionNoticeTitle}>Data Retention Notice</p>
        <p style={styles.retentionNoticeText}>
          Following departure from Enko, personal documents and personal data on file (e.g.
          address, bank details, emergency contact details, identification and other personal
          documents) are retained for <strong>6 years from the day after the last working
          date</strong>, after which they are flagged internally for review and secure removal.
          A minimal record (name, role, entity, and employment dates) is kept indefinitely for
          legitimate business and legal record-keeping purposes. This applies to all personnel
          records already on file, not only future departures.
        </p>
      </div>

      {/* Upload form */}
      {isManager && showUpload && (
        <div style={styles.uploadForm}>
          <h3 style={styles.formTitle}>Upload New Policy</h3>
          {error && <p style={styles.error}>{error}</p>}
          <div style={formGridStyle}>
            <div style={styles.field}>
              <label style={styles.label} htmlFor={`${a11yId}-title`}>Title *</label>
              
              <input id={`${a11yId}-title`} style={styles.input} value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Employee Handbook 2025" />
            </div>
            <div style={styles.field}>
              <label style={styles.label} htmlFor={`${a11yId}-entity`}>Entity *</label>
              
              <select id={`${a11yId}-entity`} style={styles.select} value={form.entity}
                onChange={e => setForm({ ...form, entity: e.target.value })}>
                {ENTITIES.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label} htmlFor={`${a11yId}-category`}>Category *</label>
              
              <select id={`${a11yId}-category`} style={styles.select} value={form.category}
                onChange={e => setForm({ ...form, category: e.target.value })}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
              <label style={styles.label} htmlFor={`${a11yId}-description`}>Description</label>
              
              <input id={`${a11yId}-description`} style={styles.input} value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Brief description (optional)" />
            </div>
            <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
              <label style={styles.label} htmlFor={`${a11yId}-file-pdf-word-excel`}>File * (PDF, Word, Excel)</label>
              
              <input id={`${a11yId}-file-pdf-word-excel`} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx"
                onChange={e => setUploadFile(e.target.files[0])} style={styles.fileInput} />
              {uploadFile && <p style={styles.fileName}>{uploadFile.name}</p>}
            </div>
          </div>
          <div style={styles.formActions}>
            <button style={styles.primaryBtn} onClick={handleUpload} disabled={uploading}>
              {uploading ? 'Uploading...' : 'Upload Policy'}
            </button>
            <button style={styles.secondaryBtn}
              onClick={() => { setShowUpload(false); setError(''); setUploadFile(null); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={styles.filters}>
        <div style={styles.filterGroup}>
          <span style={styles.filterLabel}>Category:</span>
          {['All', ...CATEGORIES].map(c => (
            <button key={c}
              style={{ ...styles.filterChip, ...(filterCategory === c ? styles.filterChipActive : {}) }}
              onClick={() => setFilterCategory(c)}>{c}</button>
          ))}
        </div>
        {isManager && (
          <div style={styles.filterGroup}>
            <span style={styles.filterLabel}>Entity:</span>
            {['All', ...ENTITIES].map(e => (
              <button key={e}
                style={{ ...styles.filterChip, ...(filterEntity === e ? styles.filterChipActive : {}) }}
                onClick={() => setFilterEntity(e)}>{e}</button>
            ))}
          </div>
        )}
      </div>

      {/* Policy list */}
      {loading ? (
        <p style={styles.empty}>Loading...</p>
      ) : loadFailed ? (
        /* LOADERR3: a genuine fetch failure, not an empty repository. PolicyRepository has no
           dark mode yet (UXAUDIT2 item 1), so the banner renders in its light variant. */
        <LoadError
          dark={false}
          message="Couldn't load the policy repository."
          onRetry={load}
        />
      ) : visible.length === 0 ? (
        <p style={styles.empty}>No policies found.</p>
      ) : (
        Object.entries(grouped).map(([entity, cats]) => (
          <div key={entity} style={styles.entitySection}>
            <h2 style={styles.entityTitle}>{entity}</h2>
            {Object.entries(cats).map(([category, pols]) => (
              <div key={category} style={styles.categoryBlock}>
                <h3 style={styles.categoryTitle}>{category}</h3>
                {pols.map(p => (
                  <div key={p.id} style={styles.policyRow}>
                    {editingId === p.id ? (
                      <div style={styles.editRow}>
                        <input style={styles.editInput} value={editForm.title}
                          onChange={e => setEditForm({ ...editForm, title: e.target.value })} />
                        <select style={styles.editSelect} value={editForm.entity}
                          onChange={e => setEditForm({ ...editForm, entity: e.target.value })}>
                          {ENTITIES.map(e => <option key={e} value={e}>{e}</option>)}
                        </select>
                        <select style={styles.editSelect} value={editForm.category}
                          onChange={e => setEditForm({ ...editForm, category: e.target.value })}>
                          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <button style={styles.saveBtn} onClick={() => handleEditSave(p.id)}>Save</button>
                        <button style={styles.secondaryBtn} onClick={() => setEditingId(null)}>Cancel</button>
                      </div>
                    ) : (
                      <>
                        <div style={styles.policyInfo}>
                          <p style={styles.policyTitle}>{p.title}</p>
                          {p.description && <p style={styles.policyDesc}>{p.description}</p>}
                          <p style={styles.policyMeta}>
                            Uploaded {toDisplayDate(p.uploaded_at).toLocaleDateString()}
                            {p.uploaded_by && ` · ${p.uploaded_by}`}
                          </p>
                        </div>
                        <div style={styles.policyActions}>
                          <button
                            onClick={() => openPolicy(p.id)}
                            disabled={policyLoading === p.id}
                            style={styles.downloadBtn}
                          >
                            {policyLoading === p.id ? 'Opening…' : 'Download'}
                          </button>
                          {isManager && (
                            <>
                              <button style={styles.editBtn}
                                onClick={() => { setEditingId(p.id); setEditForm({ title: p.title, entity: p.entity, category: p.category, description: p.description || '' }); }}>
                                Edit
                              </button>
                              <button style={styles.deleteBtn} onClick={() => handleDelete(p.id)}>Remove</button>
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
}

const styles = {
  container: { fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', maxWidth: '960px', margin: '0 auto', padding: '24px', backgroundColor: '#f8f9fa' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', gap: '16px' },
  headerLeft: { display: 'flex', alignItems: 'flex-start', gap: '16px' },
  backBtn: { backgroundColor: 'transparent', border: '1px solid #ddd', borderRadius: '6px', padding: '6px 12px', fontSize: '13px', cursor: 'pointer', color: '#333', whiteSpace: 'nowrap', marginTop: '4px' },
  // Prominent bordered treatment (matches NewStarterForm.js/SponsorForm.js/LeaverForm.js's
  // established "Back to dashboard" button style - BACKBTN-1), not the earlier low-visibility
  // plain-text link.
  dashboardBackBtn: {
    background: 'none', border: '1px solid rgba(26,39,68,0.25)', color: '#1a1a2e',
    fontSize: '14px', cursor: 'pointer', padding: '9px 18px',
    borderRadius: '6px', marginBottom: '24px', fontWeight: '600',
  },
  title: { fontSize: '24px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 4px 0' },
  subtitle: { fontSize: '13px', color: '#888', margin: '0' },
  uploadBtn: { backgroundColor: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '14px', cursor: 'pointer', whiteSpace: 'nowrap' },
  cancelBtn: { backgroundColor: '#fff', color: '#1a1a2e', border: '1px solid #1a1a2e', borderRadius: '8px', padding: '9px 20px', fontSize: '14px', cursor: 'pointer', whiteSpace: 'nowrap' },
  uploadForm: { backgroundColor: '#fff', borderRadius: '12px', padding: '24px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  retentionNotice: { backgroundColor: '#fff', borderLeft: '4px solid #c9a84c', borderRadius: '8px', padding: '14px 18px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  retentionNoticeTitle: { fontSize: '13px', fontWeight: '700', color: '#1a1a2e', margin: '0 0 6px 0' },
  retentionNoticeText: { fontSize: '12px', color: '#555', lineHeight: 1.5, margin: 0 },
  formTitle: { fontSize: '16px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 16px 0' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '11px', fontWeight: '600', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px' },
  input: { border: '1px solid #ddd', borderRadius: '6px', padding: '8px 12px', fontSize: '14px', outline: 'none' },
  select: { border: '1px solid #ddd', borderRadius: '6px', padding: '8px 12px', fontSize: '14px', outline: 'none', backgroundColor: '#fff' },
  fileInput: { fontSize: '13px' },
  fileName: { fontSize: '12px', color: '#27ae60', margin: '4px 0 0 0' },
  formActions: { display: 'flex', gap: '12px' },
  primaryBtn: { backgroundColor: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '14px', cursor: 'pointer' },
  secondaryBtn: { backgroundColor: '#fff', color: '#1a1a2e', border: '1px solid #1a1a2e', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer' },
  error: { color: '#e74c3c', fontSize: '13px', margin: '0 0 12px 0' },
  filters: { display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' },
  filterGroup: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  filterLabel: { fontSize: '12px', color: '#888', fontWeight: '600', minWidth: '60px' },
  filterChip: { backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '16px', padding: '4px 12px', fontSize: '12px', cursor: 'pointer', color: '#555' },
  filterChipActive: { backgroundColor: '#1a1a2e', border: '1px solid #1a1a2e', color: '#fff' },
  entitySection: { marginBottom: '28px' },
  entityTitle: { fontSize: '17px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 12px 0', paddingBottom: '8px', borderBottom: '2px solid #1a1a2e' },
  categoryBlock: { backgroundColor: '#fff', borderRadius: '10px', padding: '16px 20px', marginBottom: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  categoryTitle: { fontSize: '11px', fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: '0.8px', margin: '0 0 12px 0' },
  policyRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f0f0f0', gap: '16px' },
  policyInfo: { flex: 1 },
  policyTitle: { fontSize: '14px', fontWeight: '500', color: '#1a1a2e', margin: '0 0 2px 0' },
  policyDesc: { fontSize: '12px', color: '#777', margin: '0 0 3px 0' },
  policyMeta: { fontSize: '11px', color: '#667085', margin: '0' },
  policyActions: { display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 },
  downloadBtn: { backgroundColor: '#1a1a2e', color: '#fff', borderRadius: '6px', padding: '6px 14px', fontSize: '12px', textDecoration: 'none', whiteSpace: 'nowrap', display: 'inline-block' },
  editBtn: { backgroundColor: '#fff', color: '#555', border: '1px solid #ddd', borderRadius: '6px', padding: '5px 12px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' },
  deleteBtn: { backgroundColor: '#fff', color: '#e74c3c', border: '1px solid #e74c3c', borderRadius: '6px', padding: '5px 12px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' },
  editRow: { display: 'flex', gap: '8px', alignItems: 'center', flex: 1, flexWrap: 'wrap' },
  editInput: { border: '1px solid #ddd', borderRadius: '6px', padding: '6px 10px', fontSize: '13px', flex: 1, minWidth: '150px' },
  editSelect: { border: '1px solid #ddd', borderRadius: '6px', padding: '6px 8px', fontSize: '13px', backgroundColor: '#fff' },
  saveBtn: { backgroundColor: '#27ae60', color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 14px', fontSize: '12px', cursor: 'pointer' },
  empty: { color: '#888', fontStyle: 'italic', fontSize: '14px', textAlign: 'center', padding: '48px 0' },
};

export default PolicyRepository;
